import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Users, ExternalLink, Pencil, Merge, Trash2, Search, Music2, X, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getArtistDirectory, updateArtist, mergeArtists, deleteArtist } from "@/lib/afmApi";
import { Loading, SectionTitle } from "@/components/common";

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "staff"];
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";
const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

const Avatar = ({ a }) => a.image
  ? <img src={a.image} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
  : <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100"><Music2 className="h-4 w-4 text-slate-400" /></div>;

const LinkPill = ({ href, label, cls }) => href
  ? <a href={href} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label} <ExternalLink className="h-3 w-3" /></a>
  : <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-400">Sin {label}</span>;

function EditRow({ a, viewerId, onDone, onCancel }) {
  const [f, setF] = useState({ name: a.name || "", spotify_url: a.spotify_url || "", apple_url: a.apple_url || "", owner_email: a.owner_email || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await updateArtist(a.id, { viewer_id: viewerId, ...f }); toast.success("Artista actualizado"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    setBusy(false);
  };
  return (
    <div className="grid gap-2 rounded-xl border border-black bg-white p-4 sm:grid-cols-2" data-testid={`artist-edit-${a.id}`}>
      <input data-testid="artist-edit-name" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nombre artístico" />
      <input data-testid="artist-edit-owner" className={inputCls} value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} placeholder="Email del dueño (cuenta AFM)" />
      <input data-testid="artist-edit-spotify" className={inputCls} value={f.spotify_url} onChange={(e) => setF({ ...f, spotify_url: e.target.value })} placeholder="https://open.spotify.com/artist/…" />
      <input data-testid="artist-edit-apple" className={inputCls} value={f.apple_url} onChange={(e) => setF({ ...f, apple_url: e.target.value })} placeholder="https://music.apple.com/…/artist/…" />
      <div className="flex gap-2 sm:col-span-2">
        <button data-testid="artist-edit-save" disabled={busy} onClick={save} className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Guardar</button>
        <button onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-semibold hover:border-black">Cancelar</button>
      </div>
    </div>
  );
}

export default function AdminArtists() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role) || user?.super_admin;
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [sel, setSel] = useState([]);
  const [keep, setKeep] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!isAdmin) { setStatus("denied"); return; }
    getArtistDirectory(user.id).then((d) => { setItems(Array.isArray(d) ? d : []); setStatus("ok"); })
      .catch((e) => setStatus(e?.response?.status === 403 ? "denied" : "error"));
  }, [user.id, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const dupKeys = useMemo(() => {
    const c = {}; items.forEach((a) => { const k = norm(a.name); c[k] = (c[k] || 0) + 1; });
    return new Set(Object.keys(c).filter((k) => c[k] > 1));
  }, [items]);
  const filtered = items.filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()) || (a.owner_email || "").includes(q.toLowerCase()));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const doMerge = async () => {
    if (sel.length < 2 || !keep) return;
    setBusy(true);
    try {
      const r = await mergeArtists({ viewer_id: user.id, keep_id: keep, remove_ids: sel.filter((i) => i !== keep) });
      toast.success(`Fusionados: ${r.removed} duplicado(s) eliminados`);
      setSel([]); setKeep(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo fusionar"); }
    setBusy(false);
  };
  const del = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.name}" del directorio?`)) return;
    try { await deleteArtist(a.id, user.id); toast.success("Eliminado"); load(); } catch { toast.error("No se pudo eliminar"); }
  };

  if (status === "denied") return <div className="flex flex-col items-center gap-3 py-20 text-center" data-testid="artists-denied"><ShieldAlert className="h-10 w-10 text-slate-300" /><p className="text-sm text-slate-500">Solo administradores.</p></div>;
  if (status === "loading") return <Loading />;

  return (
    <div className="space-y-6" data-testid="admin-artists-page">
      <SectionTitle icon={Users} title="Directorio de Artistas" desc={`${items.length} artistas registrados · ${dupKeys.size} posibles duplicados`} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="artists-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o email del dueño…" className={`${inputCls} pl-9`} />
        </div>
        <button data-testid="artists-filter-dups" onClick={() => setQ("")} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold hover:border-black">Limpiar</button>
      </div>

      {sel.length >= 2 && (
        <div className="flex flex-col gap-3 rounded-xl border border-[#9E1B22]/30 bg-[#9E1B22]/5 p-4 sm:flex-row sm:items-center" data-testid="merge-bar">
          <div className="flex-1 text-sm"><strong>{sel.length}</strong> seleccionados. Elige cuál conservar:</div>
          <select data-testid="merge-keep" value={keep} onChange={(e) => setKeep(e.target.value)} className={`${inputCls} sm:w-64`}>
            <option value="">— Conservar —</option>
            {items.filter((a) => sel.includes(a.id)).map((a) => <option key={a.id} value={a.id}>{a.name}{a.spotify_id ? " · Spotify" : ""}</option>)}
          </select>
          <button data-testid="merge-confirm" disabled={!keep || busy} onClick={doMerge} className="flex items-center justify-center gap-1.5 rounded-lg bg-[#9E1B22] px-4 py-2 text-xs font-semibold text-white hover:bg-[#7a151b] disabled:opacity-50"><Merge className="h-3.5 w-3.5" /> Fusionar</button>
          <button onClick={() => { setSel([]); setKeep(""); }} className="rounded-lg p-2 hover:bg-white"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="space-y-2">
        {filtered.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Sin artistas.</div>}
        {filtered.map((a) => editing === a.id
          ? <EditRow key={a.id} a={a} viewerId={user.id} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
          : (
            <div key={a.id} data-testid={`artist-row-${a.id}`} className={`flex items-center gap-3 rounded-xl border bg-white p-3 ${sel.includes(a.id) ? "border-[#9E1B22]" : "border-slate-200"}`}>
              <input type="checkbox" data-testid={`artist-select-${a.id}`} checked={sel.includes(a.id)} onChange={() => toggle(a.id)} className="h-4 w-4 rounded border-slate-300" />
              <Avatar a={a} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-semibold text-black">{a.name}</span>
                  {dupKeys.has(norm(a.name)) && <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" data-testid="dup-badge">Posible duplicado</span>}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <LinkPill href={a.spotify_url} label="Spotify" cls="bg-[#1DB954]/15 text-[#15803d]" />
                  <LinkPill href={a.apple_url} label="Apple Music" cls="bg-black text-white" />
                  {a.owner_email && <span className="truncate text-[11px] text-slate-500">{a.owner_email}</span>}
                </div>
              </div>
              <button data-testid={`artist-edit-${a.id}-btn`} onClick={() => setEditing(a.id)} className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:border-black" title="Editar"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`artist-delete-${a.id}`} onClick={() => del(a)} className="rounded-lg border border-red-200 bg-red-50 p-2 text-red-600 hover:border-red-400" title="Eliminar"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
      </div>
    </div>
  );
}
